import type {
  AdminSummariesType,
  AthenaApi,
  ScheduledReportType,
  SystemOptionType,
  TimezoneOptionType
} from '@/shared/lib/types/administration';
import type { AccountGoalType } from '@/shared/lib/types/goal';
import type {
  ClientOrganizationType,
  RelyingPartyAdminSessionType,
  RelyingPartyAdminType,
  RelyingPartyPerformanceReportType,
  TaskType
} from '@/shared/lib/types/graphql';
import type Meta from '@/shared/lib/types/meta';
import type { ProviderType } from '@/shared/lib/types/remotePatientMonitoring';
import { gql } from '@apollo/client';

export type AddUpdateClientOrgResponse = {
  saveOrganization: {
    clientOrganization: Partial<ClientOrganizationType>;
  };
};

export const ADD_UPDATE_CLIENT_ORG = gql`
  mutation createOrganization(
    $organizationId: String
    $organizationData: ClientOrganizationInput!
    $clientStoreInputs: [ClientStoreInput!]
    $relyingPartyId: String
  ) {
    saveOrganization(
      organizationId: $organizationId
      organizationData: $organizationData
      clientStoreInputs: $clientStoreInputs
      relyingPartyId: $relyingPartyId
    ) {
      clientOrganization {
        id
      }
    }
  }
`;

export type GetClientOrganizationResponse = {
  clientOrganization: ClientOrganizationType;
};

export const GET_CLIENT_ORGANIZATION_LOCATIONS = gql`
  query getClientOrganizationQuery($clientOrganizationId: String!) {
    clientOrganization(clientOrganizationId: $clientOrganizationId) {
      id
      name
      createdAt
      clientStores {
        id
        name
        nameTag
        isPrimary
      }
    }
  }
`;

export const GET_CLIENT_ORGANIZATION = gql`
  query getClientOrganizationQuery($clientOrganizationId: String!) {
    clientOrganization(clientOrganizationId: $clientOrganizationId) {
      id
      name
      createdAt
      addresses {
        city
        line1
      }
      contacts {
        type
        value
      }
      tasks {
        id
        name
        type
        subType
        status
        billedAmount
        healthPlan
        completedByAdmin
        location
        resolutionStatus
        resolutionNotes
        performedOn
        account {
          truentityId
        }
        encounters {
          id
        }
      }
      accounts {
        truentityId
        id
        address
        birthDate
        gender
        user {
          id
          firstName
          lastName
        }
        accountsAssignments {
          assignedAt
          relyingPartyAdmin {
            name
          }
        }
      }
      clientStores {
        id
        name
        nameTag
      }
    }
  }
`;

export const GET_CLIENT_ORGANIZATION_ACCOUNTS = gql`
  query getClientOrganizationAccountsQuery($clientOrganizationId: String!) {
    clientOrganizationAccounts(clientOrganizationId: $clientOrganizationId) {
      truentityId
      id
      address
      birthDate
      gender
      user {
        id
        firstName
        lastName
      }
      accountsAssignments {
        assignedAt
        relyingPartyAdmin {
          name
        }
      }
    }
  }
`;

export const GET_CLIENT_ORGANIZATIONS = gql`
  query getClientOrganizationsQuery($relyingPartyId: ID, $pageNum: Int!, $pageSize: Int!) {
    clientOrganizations(relyingPartyId: $relyingPartyId, pageNum: $pageNum, pageSize: $pageSize) {
      clientOrganizations {
        id
        name
        createdAt
        addresses {
          city
          line1
        }
        tasksCount
        accountsCount
        clientStoresCount
        clientStores {
          id
          name
          nameTag
        }
      }
      meta {
        totalCount
      }
    }
  }
`;

export type GetClientOrganizationsResponse = {
  clientOrganizations: {
    clientOrganizations: ClientOrganizationType[];
    meta: Meta;
  };
};

export const GET_CLIENT_ORGANIZATIONS_STANDARD = gql`
  query getClientOrganizationsQuery($relyingPartyId: ID, $pageNum: Int!, $pageSize: Int!) {
    clientOrganizations(relyingPartyId: $relyingPartyId, pageNum: $pageNum, pageSize: $pageSize) {
      clientOrganizations {
        id
        name
        settings
        createdAt
        addresses {
          city
          line1
        }
        clientStores {
          id
          name
          nameTag
          isPrimary
          settings
        }
      }
      meta {
        totalCount
      }
    }
  }
`;

export const GET_CLIENT_STORE = gql`
  query getClientStoreQuery($clientStoreId: String!) {
    clientStore(clientStoreId: $clientStoreId) {
      id
      name
      nameTag
      createdAt
      addresses {
        city
        line1
      }
      contacts {
        type
        value
      }
      tasks {
        id
        name
        type
        subType
        status
        resolutionStatus
        resolutionNotes
        completedByAdmin
        status
        billedAmount
        healthPlan
        location
        performedOn
        account {
          truentityId
        }
        encounters {
          id
        }
      }
      accounts {
        truentityId
        id
        address
        birthDate
        gender
        user {
          id
          firstName
          lastName
        }
        accountsAssignments {
          assignedAt
          relyingPartyAdmin {
            name
          }
        }
      }
    }
  }
`;

export const GET_CLIENT_STORES = gql`
  query GetClientStoresQuery($pageNum: Int!, $pageSize: Int!) {
    clientStores(pageNum: $pageNum, pageSize: $pageSize) {
      clientStores {
        id
        name
        clientOrg {
          name
          id
        }
        tasks {
          id
          name
          type
          subType
          status
          resolutionStatus
          resolutionNotes
          completedByAdmin
          status
          billedAmount
          healthPlan
          location
          performedOn
          encounters {
            id
          }
        }
      }
    }
  }
`;

export type GetClientStoresStandardResponse = {
  clientStores: {
    clientStores: ClientOrganizationType[];
  };
};

export const GET_CLIENT_STORES_STANDARD = gql`
  query GetClientStoresQuery($pageNum: Int!, $pageSize: Int!) {
    clientStores(pageNum: $pageNum, pageSize: $pageSize) {
      clientStores {
        id
        name
        clientOrg {
          name
          id
        }
      }
    }
  }
`;

export const GET_CLIENT_STORES_BY_ORG = gql`
  query GetClientStoresByOrgQuery($clientOrgId: String!, $pageNum: Int!, $pageSize: Int!) {
    clientStoresByOrg(clientOrgId: $clientOrgId, pageNum: $pageNum, pageSize: $pageSize) {
      clientStores {
        id
        name
        nameTag
        tasks {
          id
          name
          type
          subType
          status
          resolutionStatus
          resolutionNotes
          completedByAdmin
          status
          billedAmount
          healthPlan
          location
          performedOn
          encounters {
            id
          }
        }
      }
    }
  }
`;

export const ADD_CLIENT_STORE = gql`
  mutation AddClientStore($clientOrgId: ID!, $storeInput: ClientStoreInput!) {
    addClientStore(clientOrgId: $clientOrgId, storeInput: $storeInput) {
      status
      message
    }
  }
`;

export const UPDATE_CLIENT_STORE = gql`
  mutation UpdateClientStore($clientStoreId: ID!, $name: String!, $nameTag: String!, $isPrimary: Boolean, $settings: JSON) {
    updateClientStore(clientStoreId: $clientStoreId, name: $name, nameTag: $nameTag, isPrimary: $isPrimary, settings: $settings) {
      status
      message
    }
  }
`;

export const ASSIGN_PATIENTS_TO_ORGANIZATIONS = gql`
  mutation assignAccountsToOrganization($organizationId: String!, $accountIds: [String!]) {
    assignAccountsToOrganization(organizationId: $organizationId, accountIds: $accountIds) {
      status
    }
  }
`;

export const ASSIGN_CLIENT_ORGS_STORES_TO_ACCOUNT = gql`
  mutation assignClientOrgsStoresToAccount($truentityId: String!, $clientOrganizationStoreData: [ClientOrganizationInput!]!) {
    assignClientOrgsStoresToAccount(truentityId: $truentityId, clientOrganizationStoreData: $clientOrganizationStoreData) {
      status
      message
    }
  }
`;

export type AssignAdminsToPatientsResponse = {
  assignAdminsToPatients: {
    status: string;
  };
};
export const ASSIGN_ADMINS_TO_PATIENTS = gql`
  mutation AssignAdminsToPatients($accountIds: [String!], $relyingPartyAdminIds: [String!]) {
    assignAdminsToPatients(accountIds: $accountIds, relyingPartyAdminIds: $relyingPartyAdminIds) {
      status
      message
    }
  }
`;

export const UNASSIGN_ADMINS_TO_PATIENTS = gql`
  mutation UnassignAdminsToPatients($accountIds: [String!]) {
    unassignAdminsToPatients(accountIds: $accountIds) {
      status
      message
    }
  }
`;

export const GET_RELYING_PARTY_ADMIN_BY_ID = gql`
  query RelyingPartyAdminById($relyingPartyAdminId: String!) {
    relyingPartyAdminById(relyingPartyAdminId: $relyingPartyAdminId) {
      id
      user {
        id
        firstName
        lastName
        email
      }
      contactsRelyingPartyAdmins {
        contactType
        value
        relyingPartyAdminId
      }
      roleType
      isObserver
      isRpmPrimary
      isOwner
      npiNumber
      externalDialpadId
      smsProvider
      messagingPhoneNumber
      retainedDialpadPhoneNumber
      techLicense
      title
      providerTitle
      hourlyRate
      supervisorEmail
      businessName
      addressLine1
      addressLine2
      addressZipcode
      addressCity
      addressState
      phone
      alternateEmail
      bankName
      bankAccountNumber
      bankRoutingNumber
      settings
      presignedAvatarUrl
    }
  }
`;

export const GET_RELYING_PARTY_RPM_REVIEWER = gql`
  query GetRpmReviewer {
    getRpmReviewer {
      id
      isObserver
      isRpmPrimary
    }
  }
`;

export const GET_RELYING_PARTY_ADMINS = gql`
  query GetRelyingPartyAdminsAll($pageNum: Int!, $pageSize: Int!) {
    relyingPartyAdmins(pageNum: $pageNum, pageSize: $pageSize) {
      relyingPartyAdmins {
        id
        user {
          id
          firstName
          lastName
          email
        }
        contactsRelyingPartyAdmins {
          contactType
          value
          relyingPartyAdminId
        }
        roleType
        npiNumber
        isObserver
        isRpmPrimary
        isOwner
        externalDialpadId
        techLicense
        title
        hourlyRate
        supervisorEmail
        businessName
        addressLine1
        addressLine2
        addressZipcode
        addressCity
        addressState
        phone
        alternateEmail
        bankName
        bankAccountNumber
        bankRoutingNumber
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type AddRelyingPartyAdminResponse = {
  addRelyingPartyAdmin: {
    status: string;
    message: string;
  };
};

export const ADD_RELYING_PARTY_ADMIN = gql`
  mutation addRelyingPartyAdmin(
    $email: Email!
    $firstName: String!
    $lastName: String!
    $roleType: String!
    $isObserver: Boolean!
    $isRpmPrimary: Boolean
    $isOwner: Boolean
    $organization: String!
    $techLicense: String!
    $npiNumber: String!
    $title: String!
    $providerTitle: String
    $isTextLoginOtp: Boolean!
    $isEmailLoginOtp: Boolean!
    $mobileNumber: String!
    $relyingPartyId: String
    $supervisingProviderId: String
  ) {
    addRelyingPartyAdmin(
      email: $email
      firstName: $firstName
      lastName: $lastName
      roleType: $roleType
      isObserver: $isObserver
      isRpmPrimary: $isRpmPrimary
      isOwner: $isOwner
      organization: $organization
      techLicense: $techLicense
      npiNumber: $npiNumber
      title: $title
      providerTitle: $providerTitle
      isEmailLoginOtp: $isEmailLoginOtp
      isTextLoginOtp: $isTextLoginOtp
      mobileNumber: $mobileNumber
      relyingPartyId: $relyingPartyId
      supervisingProviderId: $supervisingProviderId
    ) {
      status
      message
    }
  }
`;

export type UpdateRelyingPartyAdminSupervisingProviderIdResponse = {
  updateRelyingPartyAdminSupervisingProviderId: {
    status: string;
    message: string;
  };
};

export const UPDATE_RELYING_PARTY_ADMIN_SUPERVISING_PROVIDER_ID = gql`
  mutation UpdateRelyingPartyAdminSupervisingProviderId($relyingPartyAdminId: ID!, $supervisingProviderId: String) {
    updateRelyingPartyAdminSupervisingProviderId(relyingPartyAdminId: $relyingPartyAdminId, supervisingProviderId: $supervisingProviderId) {
      status
      message
    }
  }
`;

export const DEACTIVATE_RELYING_PARTY_ADMIN = gql`
  mutation DeactivateRelyingPartyAdmin($isActive: Boolean!, $relyingPartyAdminId: String!) {
    deactivateRelyingPartyAdmin(isActive: $isActive, relyingPartyAdminId: $relyingPartyAdminId) {
      status
      message
    }
  }
`;

export type CommonPerformanceStatStringType = {
  dailyValue: string;
  weeklyValue: string;
  monthlyValue: string;
};

export type CommonPerformanceStatNumType = {
  dailyValue: number;
  weeklyValue: number;
  monthlyValue: number;
};

export type GetAdminPerformancesResponse = {
  getAdminPerformances: {
    dailyLabel: string;
    weeklyLabel: string;
    monthlyLabel: string;
    tipsCompleted: CommonPerformanceStatNumType;
    cmrsCompleted: CommonPerformanceStatNumType;
    encounters: CommonPerformanceStatNumType;
    tcmsCompleted: CommonPerformanceStatNumType;
    rpmsCompleted: CommonPerformanceStatNumType;
    invoicedTime: CommonPerformanceStatStringType;
    utilizationRate: CommonPerformanceStatNumType;
  };
};

export const GET_ADMIN_PERFORMANCE = gql`
  query GetAdminPerformancesQuery($relyingPartyAdminId: String!) {
    getAdminPerformances(relyingPartyAdminId: $relyingPartyAdminId) {
      dailyLabel
      weeklyLabel
      monthlyLabel
      tipsCompleted {
        dailyValue
        weeklyValue
        monthlyValue
      }
      cmrsCompleted {
        dailyValue
        weeklyValue
        monthlyValue
      }
      encounters {
        dailyValue
        weeklyValue
        monthlyValue
      }
      tcmsCompleted {
        dailyValue
        weeklyValue
        monthlyValue
      }
      rpmsCompleted {
        dailyValue
        weeklyValue
        monthlyValue
      }
      invoicedTime {
        dailyValue
        weeklyValue
        monthlyValue
      }
      utilizationRate {
        dailyValue
        weeklyValue
        monthlyValue
      }
    }
  }
`;

/** `GET_HEALTH_PLANS` -> healthPlans.healthPlans (mirrors the selection set exactly). */
export type HealthPlanOption = { id: string; orgName: string };

export const GET_HEALTH_PLANS = gql`
  query getHealthPlans($pageNum: Int!, $pageSize: Int!) {
    healthPlans(pageNum: $pageNum, pageSize: $pageSize) {
      healthPlans {
        id
        orgName
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const ADD_HEALTH_PLAN = gql`
  mutation addHealthPlan($name: String!) {
    addHealthPlan(name: $name) {
      status
      message
    }
  }
`;

export const UPDATE_HEALTH_PLAN = gql`
  mutation updateHealthPlan($healthPlanId: ID!, $name: String!) {
    updateHealthPlan(healthPlanId: $healthPlanId, name: $name) {
      status
      message
    }
  }
`;

export type GetRelyingPartyAdminSessionsResponse = {
  relyingPartyAdminSessions: {
    relyingPartyAdminSessions: RelyingPartyAdminSessionType[];
  };
};
export const GET_RELYING_PARTY_ADMINS_SESSIONS = gql`
  query GetRelyingPartyAdminSessions($relyingPartyAdminId: ID!, $pageNum: Int!, $pageSize: Int!) {
    relyingPartyAdminSessions(relyingPartyAdminId: $relyingPartyAdminId, pageNum: $pageNum, pageSize: $pageSize) {
      relyingPartyAdminSessions {
        id
        startedAt
        endedAt
        durationInSecs
        device {
          userAgent
          ip
        }
        timedOut
      }
    }
  }
`;

export const GET_RELYING_PARTY_ADMINS_ACTIVITIES = gql`
  query GetRelyingPartyAdminActivitiesBySession($relyingPartyAdminSessionId: ID!, $pageNum: Int!, $pageSize: Int!) {
    relyingPartyAdminActivitiesBySession(relyingPartyAdminSessionId: $relyingPartyAdminSessionId, pageNum: $pageNum, pageSize: $pageSize) {
      activityLogs {
        id
        logText
        activityType
        activityTime
        activityEndTime
        timeTakenSecs
        account {
          truentityId
          user {
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export type RelyingPartyAdminAllResponseType = {
  relyingPartyAdmins: {
    relyingPartyAdmins: RelyingPartyAdminType[];
  };
};

export const GET_RELYING_PARTY_ADMINS_ALL = gql`
  query GetRelyingPartyAdminsAll($pageNum: Int!, $pageSize: Int!) {
    relyingPartyAdmins(pageNum: $pageNum, pageSize: $pageSize) {
      relyingPartyAdmins {
        id
        name
        user {
          id
          firstName
          lastName
          email
        }
        contactsRelyingPartyAdmins {
          contactType
          value
          relyingPartyAdminId
        }
        roleType
        npiNumber
        title
        techLicense
        hourlyRate
        supervisorEmail
        businessName
        addressLine1
        addressLine2
        addressZipcode
        addressCity
        addressState
        phone
        alternateEmail
        bankName
        bankAccountNumber
        bankRoutingNumber
        lastSession {
          id
          startedAt
          endedAt
          durationInSecs
          device {
            userAgent
            ip
          }
          timedOut
        }
        minutesToday
        minutesThisWeek
        minutesThisMonth
        minutesThisYear
        activeSecsToday
        activeSecsThisMonth
        activityToday
        activityThisWeek
        activityThisMonth
        activityThisYear
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const GET_RELYING_PARTY_PHARMACISTS = gql`
  query GetRelyingPartyPharmacistsAll($pageNum: Int!, $pageSize: Int!) {
    relyingPartyAdminsAll(pageNum: $pageNum, pageSize: $pageSize) {
      relyingPartyAdmins {
        id
        user {
          id
          firstName
          lastName
          email
        }
        contactsRelyingPartyAdmins {
          contactType
          value
          relyingPartyAdminId
        }
        name
        roleType
        npiNumber
        techLicense
        title
        hourlyRate
        supervisorEmail
        businessName
        addressLine1
        addressLine2
        addressZipcode
        addressCity
        addressState
        phone
        alternateEmail
        bankName
        bankAccountNumber
        bankRoutingNumber
      }
    }
  }
`;

export type GetRelyingPartyPerformanceReportsResponse = {
  performanceReport: {
    data: RelyingPartyPerformanceReportType[];
    meta: Meta;
  };
};

export const GET_RELYING_PARTY_PERFORMANCE_REPORTS = gql`
  mutation PerformanceReport(
    $startDate: String!
    $endDate: String!
    $relyingPartyAdminId: String!
    $isExport: Boolean!
    $pageNum: Int!
    $pageSize: Int!
  ) {
    performanceReport(
      startDate: $startDate
      endDate: $endDate
      relyingPartyAdminId: $relyingPartyAdminId
      isExport: $isExport
      pageNum: $pageNum
      pageSize: $pageSize
    ) {
      data {
        date
        activeTime
        billedAmount
        bonusAmount
        numOfEncounters
        invoicedTime
        invoicedAmount
        relyingPartyAdminName
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type GetUserTaskPerformanceResponseType = {
  getUsersTask: {
    tasks: TaskType[];
    meta: Meta;
  };
};

export const GET_USER_TASK_PERFORMANCE = gql`
  query GetUsersTaskPerformance($relyingPartyAdminId: ID, $startDate: ISO8601Date!, $pageNum: Int, $pageSize: Int) {
    getUsersTask(relyingPartyAdminId: $relyingPartyAdminId, startDate: $startDate, pageNum: $pageNum, pageSize: $pageSize) {
      tasks {
        id
        type
        status
        wrapUpStatus
        modeOfResolution
        performedOn
        billedAmount
        bonusAmount
        encounters {
          originatingClientOrg {
            name
          }
          originatingClientStore {
            name
          }
        }
        account {
          user {
            firstName
            lastName
          }
          birthDate
          currentRelyingPartyClientOrgs {
            name
          }
          currentRelyingPartyClientStores {
            name
          }
        }
        mtmCaseNumber
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type GetRPAgreementResponse = {
  relyingPartyAgreement: {
    status: string;
    agreementUrl: string;
  };
};

export const GET_RELYING_PARTY_AGREEMENT = gql`
  query GetRelyingPartyAgreement {
    relyingPartyAgreement {
      status
      agreementUrl
    }
  }
`;

export type ClickThroughAgreementResponse = {
  respondClickThroughAgreement: {
    status: string;
    message: string;
  };
};

export type CTAUserDetailsInput = {
  ipAddress: string;
};

export const RESPOND_CLICK_THROUGH_AGREEMENT = gql`
  mutation RespondClickThroughAgreement($action: AgreementRespondTypeEnum!, $userDetails: CtaUserDetailsInput!) {
    respondClickThroughAgreement(action: $action, userDetails: $userDetails) {
      status
      message
    }
  }
`;

export type CreateScheduledReportResponse = {
  createScheduledReport: {
    status: string;
    message: string;
  };
};

export const CREATE_SCHEDULED_REPORT = gql`
  mutation CreateScheduledReport($reportInputs: [ScheduleReportInput!]!) {
    createScheduledReport(reportInputs: $reportInputs) {
      status
      message
    }
  }
`;

export type GetScheduledRpoertsResponse = {
  getScheduledReports: {
    scheduledReports: ScheduledReportType[];
    meta: Meta;
  };
};

export const GET_SCHEDULED_REPORTS = gql`
  query GetScheduledReports($filterOptions: ScheduledReportsFilterOptionsInput!, $pageNum: Int!, $pageSize: Int!) {
    getScheduledReports(filterOptions: $filterOptions, pageNum: $pageNum, pageSize: $pageSize) {
      scheduledReports {
        id
        name
        organization
        status
        reportType
        s3Url
        createdAt
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type PinSettingsInputType = {
  securityPin: string | undefined;
  inactivityPinPromptTimeoutSeconds: number;
  inactivityTimeoutSeconds?: number;
};

export const UPDATE_RELYING_PARTY_ADMIN_PIN_SETTINGS = gql`
  mutation UpdateRelyingPartyAdminPinSettings($relyingPartyAdminId: String!, $pinSettingsInput: RelyingPartyAdminPinSettingsInput!) {
    updateRelyingPartyAdminPinSettings(relyingPartyAdminId: $relyingPartyAdminId, pinSettingsInput: $pinSettingsInput) {
      status
      message
    }
  }
`;

export type RelyingPartyAdminSecuritySettingsResponse = {
  relyingPartyAdminSecuritySettings: {
    isSecurityPinSet: boolean;
    inactivityPinPromptTimeoutSeconds: number;
    inactivityTimeoutSeconds: number;
  };
};

export const GET_RELYING_PARTY_ADMIN_SECURITY_SETTINGS = gql`
  query RelyingPartyAdminSecuritySettings($relyingPartyAdminId: String!) {
    relyingPartyAdminSecuritySettings(relyingPartyAdminId: $relyingPartyAdminId) {
      isSecurityPinSet
      inactivityTimeoutSeconds
      inactivityPinPromptTimeoutSeconds
    }
  }
`;

export type RevokeTrustedDevicesResponse = {
  revokeTrustedDevices: {
    status: string;
    message: string;
  };
};

export const REVOKE_TRUSTED_DEVICES = gql`
  mutation RevokeTrustedDevices($relyingPartyAdminId: String) {
    revokeTrustedDevices(relyingPartyAdminId: $relyingPartyAdminId) {
      status
      message
    }
  }
`;

export type RelyingPartyIdleTimeoutReLoginResponse = {
  relyingPartyIdleTimeoutReLogin: {
    status: string;
    code?: string;
    message: string;
    relyingPartyAdmin: {
      id: string;
      roleType: string;
      relyingParty: {
        id: string;
        name: string;
      };
    };
  };
};

export const RELYING_PARTY_IDLE_TIMEOUT_RE_LOGIN = gql`
  mutation RelyingPartyIdleTimeoutReLogin($pinCode: String!) {
    relyingPartyIdleTimeoutReLogin(pinCode: $pinCode) {
      status
      code
      message
      relyingPartyAdmin {
        id
        roleType
        relyingParty {
          id
          name
        }
      }
    }
  }
`;

export type ProviderStatisticsDataType = {
  approvedCount: string;
  skippedCount: string;
  rejectedCount: string;
  reportsReviewedCount: string;
  totalCount: string;
  signedOffCount: string;
  monthYear: string;
  provider: ProviderType;
};

export type GetRpmProviderStatisticsResponse = {
  getRpmProviderStatistics: {
    providerStatistics: ProviderStatisticsDataType[];
    meta: Meta;
  };
};

export const GET_RPM_PROVIDER_STATISTICS = gql`
  query GetRpmProviderStatistics($pageNum: Int!, $pageSize: Int!) {
    getRpmProviderStatistics(pageNum: $pageNum, pageSize: $pageSize) {
      providerStatistics {
        approvedCount
        skippedCount
        rejectedCount
        reportsReviewedCount
        totalCount
        monthYear
        signedOffCount
        provider {
          id
          individualFirstName
          individualLastName
        }
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export type CreateScheduledRPMPerformanceReportResponse = {
  createRelyingPartiesPerformanceReport: {
    status: string;
    message: string;
  };
};

export const CREATE_SCHEDULED_RPM_PERFORMANCE_REPORT = gql`
  mutation createRelyingPartiesPerformanceReport($monthYear: String!) {
    createRelyingPartiesPerformanceReport(monthYear: $monthYear) {
      status
      message
    }
  }
`;

export type GetAthenaMckApisResponse = {
  getAthenaApis: AthenaApi[];
};

export const GET_ATHENA_MOCK_APIS = gql`
  query GetAthenaApis {
    getAthenaApis {
      name
      endpoint
      category
      endpointMethod
      description
      serviceTag
      athenaVerified
      parameters {
        name
        type
        defaultValue
        required
        description
      }
    }
  }
`;

export const TRIGGER_ATHENA_MOCK_API = gql`
  mutation TriggerAthenaEndpoints($apiName: String!, $parameters: JSON!) {
    triggerAthenaEndpoints(apiName: $apiName, parameters: $parameters) {
      status
      response
      error
    }
  }
`;

export type GetSystemOptionsResponse = {
  getSystemOptions: {
    options: SystemOptionType[];
    optionValues: Record<string, string | null>;
  };
};

export const GET_SYSTEM_OPTIONS = gql`
  query GetSystemOptions($optionKey: String!) {
    getSystemOptions(optionKey: $optionKey) {
      optionValues
      options {
        value
        defaultContactMode
        copilotCategory
        isOptionsEditable
        applicablePrograms
        nestedOptions {
          value
          defaultContactMode
          applicablePrograms
        }
        prioritizationFilters
      }
    }
  }
`;

export type GetAccountCareTeamTaskResponse = {
  getAccountGoals: {
    goals: AccountGoalType[];
  };
};

export const GET_ACCOUNT_CARE_TEAM_TASKS = gql`
  query GetAccountGoals($truentityId: ID!, $pageNum: Int, $pageSize: Int) {
    getAccountGoals(truentityId: $truentityId, pageNum: $pageNum, pageSize: $pageSize) {
      goals {
        id
        goalContent
        goal {
          category
          subCategory
        }
        careTeamTasks {
          id
          taskContent
        }
      }
    }
  }
`;

export type GetTimezoneOptionsResponse = {
  getTimezoneOptions: {
    options: TimezoneOptionType[];
    meta: Meta;
  };
};

export const GET_TIMEZONE_OPTIONS = gql`
  query GetTimezoneOptions {
    getTimezoneOptions {
      options {
        name
        offset
      }
      meta {
        totalCount
      }
    }
  }
`;

export type GetPatentSessionTimeResponse = {
  activeTimeByAdminMetrics: {
    adminSummaries: AdminSummariesType[];
    meta: Meta;
  };
};

export const GET_PATIENT_SESSION_TIME = gql`
  query ActiveTimeByAdminMetricsQuery(
    $relyingPartyId: ID
    $startDate: ISO8601Date!
    $endDate: ISO8601Date!
    $excludedRoles: [AdminRoleTypeEnum!]
    $includePatientContextActivities: Boolean
    $includeCareActivitySummary: Boolean
    $pageNum: Int
    $pageSize: Int
  ) {
    activeTimeByAdminMetrics(
      relyingPartyId: $relyingPartyId
      startDate: $startDate
      endDate: $endDate
      excludedRoles: $excludedRoles
      includePatientContextActivities: $includePatientContextActivities
      includeCareActivitySummary: $includeCareActivitySummary
      pageNum: $pageNum
      pageSize: $pageSize
    ) {
      adminSummaries {
        relyingPartyAdmin {
          id
          user {
            firstName
            lastName
            email
          }
          title
        }
        totalActiveSeconds
        patientContextActivities {
          account {
            truentityId
            user {
              firstName
              lastName
            }
          }
          totalActiveSeconds
        }
        careActivitySummary {
          totalTimeSpentSecs
        }
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export const EXPORT_PATIENT_SESSION_TIME = gql`
  query ActiveTimeByAdminMetricsQuery(
    $relyingPartyId: ID
    $startDate: ISO8601Date!
    $endDate: ISO8601Date!
    $excludedRoles: [AdminRoleTypeEnum!]
    $includePatientContextActivities: Boolean
    $includeCareActivitySummary: Boolean
  ) {
    activeTimeByAdminMetrics(
      relyingPartyId: $relyingPartyId
      startDate: $startDate
      endDate: $endDate
      excludedRoles: $excludedRoles
      includePatientContextActivities: $includePatientContextActivities
      includeCareActivitySummary: $includeCareActivitySummary
    ) {
      adminSummaries {
        relyingPartyAdmin {
          id
          user {
            firstName
            lastName
            email
          }
          title
          settings
        }
        totalActiveSeconds
        patientContextActivities {
          account {
            truentityId
            user {
              firstName
              lastName
            }
          }
          totalActiveSeconds
          usedActivityLogs {
            activityType
            activityTime
            activityEndTime
            timeTakenSecs
            calculatedDurationSecs
          }
        }
        careActivitySummary {
          totalTimeSpentSecs
          careActivities {
            relyingParty {
              name
            }
            startedAt
            title
            subType
            totalTimeSpentSecs
          }
        }
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export type CreateAccountMedicalSubServiceResponse = {
  createAccountMedicalSubService: {
    medicalSubServiceId: string;
    status: string;
    message: string;
  };
};

export const CREATE_ACCOUNT_MEDICAL_SUB_SERVICE = gql`
  mutation CreateAccountMedicalSubService(
    $truentityId: String!
    $medicalServiceName: String!
    $diagnosisIds: [ID!]!
    $unsubscribe: Boolean
    $skipAccountMedicalSubService: Boolean
  ) {
    createAccountMedicalSubService(
      truentityId: $truentityId
      medicalServiceName: $medicalServiceName
      diagnosisIds: $diagnosisIds
      unsubscribe: $unsubscribe
      skipAccountMedicalSubService: $skipAccountMedicalSubService
    ) {
      medicalSubServiceId
      status
      message
    }
  }
`;

export type UpdateDataGridPreferenceResponse = {
  updateDataGridPreference: {
    status: string;
    message: string;
    preferences: string;
  };
};

export const UPDATE_DATA_GRID_PREFERENCE = gql`
  mutation UpdateDataGridPreference($gridName: String!, $preferences: String!) {
    updateDataGridPreference(gridName: $gridName, preferences: $preferences) {
      status
      message
      preferences
    }
  }
`;

export const RESET_DATA_GRID_PREFERENCE = gql`
  mutation ResetDataGridPreference($gridName: String!) {
    resetDataGridPreference(gridName: $gridName) {
      status
      message
      preferences
    }
  }
`;

export const PREVIEW_PAYER_CHART_REQUEST_MATCHES = gql`
  mutation PreviewPayerChartRequestMatches($file: Upload!, $offset: Int, $limit: Int) {
    previewPayerChartRequestMatches(file: $file, offset: $offset, limit: $limit) {
      fileRowCount
      rows {
        rowNumber
        requestType
        firstName
        lastName
        dateOfBirth
        serviceStartDate
        serviceEndDate
        matchStatus
        error
        candidates {
          truentityId
          firstName
          lastName
          dateOfBirth
          relyingPartyNames
        }
      }
    }
  }
`;

export const CREATE_PAYER_CHART_REQUEST = gql`
  mutation CreatePayerChartRequest(
    $requestName: String!
    $insuranceProviderName: String!
    $patients: [PayerChartRequestPatientInput!]!
    $fileNameFormat: String
  ) {
    createPayerChartRequest(
      requestName: $requestName
      insuranceProviderName: $insuranceProviderName
      patients: $patients
      fileNameFormat: $fileNameFormat
    ) {
      status
      message
      payerChartRequestId
    }
  }
`;

export const GET_INTEGRATION_REQUEST_LOGS = gql`
  query IntegrationRequestLogs(
    $serviceName: IntegrationLogServiceEnum!
    $startDate: ISO8601Date
    $endDate: ISO8601Date
    $success: Boolean
    $lowConfidence: Boolean
    $pageNum: Int!
    $pageSize: Int!
  ) {
    integrationRequestLogs(
      serviceName: $serviceName
      startDate: $startDate
      endDate: $endDate
      success: $success
      lowConfidence: $lowConfidence
      pageNum: $pageNum
      pageSize: $pageSize
    ) {
      integrationRequestLogs {
        id
        createdAt
        serviceName
        success
        durationMs
        httpStatus
        errorClass
        errorMessage
        relyingPartyName
        adminName
        requestMetadata
        responseMetadata
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export const GET_PAYER_CHART_REQUESTS = gql`
  query PayerChartRequests($pageNum: Int!, $pageSize: Int!) {
    payerChartRequests(pageNum: $pageNum, pageSize: $pageSize) {
      payerChartRequests {
        id
        requestName
        insuranceProviderName
        status
        createdAt
        downloadUrl
        requestedBy {
          name
        }
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;
